import { useTasks } from '@app/context/TaskContext';
import { useUser } from '@app/context/UserContext';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { calculateApplicationStatus, computeWeightedProgress } from '@app/utils/progress';
import { calculateTopicStatus } from '@app/utils/topicStatus';
import { Task } from '@dieterApi/task/useTaskQuery';
import {
  ApplicationStatus,
  ApplicationType,
  UserApplicationQuestionnaires,
  UserQuestionnaire,
} from '@dieterApi/user/useUserQuery';
import { useUserTenantsQuery } from '@dieterApi/user/useUserTenantsQuery';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useMemo, useState } from 'react';

interface TopicAggregate {
  id: string;
  title: string;
  status: ApplicationStatus;
  questionnaires: UserQuestionnaire[];
  lastUpdated?: string;
  counts: Record<'COMPLETE' | 'TOUCHED' | 'UNTOUCHED', number> & { total: number };
  applications: UserApplicationQuestionnaires[]; // raw applications from API (mixed type acceptable for report view)
}

export function MultiTenantReport() {
  const { user } = useUser();
  const { userValues } = useUserValuesData();
  const tasks = useTasks();

  const { data, loading, error } = useUserTenantsQuery();

  const companies = data?.getUser.companies || [];
  const [companyId, setCompanyId] = useState<string | undefined>(companies[0]?.id);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => setCompanyId(e.target.value as string);
  const toggleTopic = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const activeCompany = companies.find((c) => c.id === companyId) || companies[0];

  const topicAggregates: TopicAggregate[] = useMemo(() => {
    if (!activeCompany) return [];
    // The query returns topics under each company with applications + questionnaires
    // We aggregate questionnaire states & compute status using calculateTopicStatus
    // (status prop not present on topic object in this query, so we rely on calculation)
    // @ts-ignore - topic here lacks status; calculateTopicStatus only uses status if present.
    return activeCompany.topics.map((topic) => {
      const applications =
        topic.applications.filter((a) => [ApplicationType.Primary, ApplicationType.Secondary].includes(a.type)) || [];
      const questionnaires: UserQuestionnaire[] = applications
        ? applications.flatMap((app) => app.questionnaires || [])
        : [];
      const lastUpdated = questionnaires
        .map((q) => q.updatedAt)
        .filter(Boolean)
        .sort()
        .slice(-1)[0];
      const counts = {
        COMPLETE: questionnaires.filter((q) => q.state === QuestionnaireState.COMPLETE).length,
        TOUCHED: questionnaires.filter((q) => q.state === QuestionnaireState.TOUCHED).length,
        UNTOUCHED: questionnaires.filter((q) => q.state === QuestionnaireState.UNTOUCHED).length,
        total: questionnaires.length,
      } as TopicAggregate['counts'];
      const status = calculateTopicStatus(topic, false, userValues, tasks);
      return {
        id: topic.id,
        title: topic.title || topic.id,
        status,
        questionnaires,
        lastUpdated,
        counts,
        applications,
      };
    });
  }, [activeCompany, userValues, tasks]);

  const overall = useMemo(() => {
    if (!topicAggregates.length) return null;
    const totalQuestionnaires = topicAggregates.reduce((sum, t) => sum + t.counts.total, 0);
    const complete = topicAggregates.reduce((sum, t) => sum + t.counts.COMPLETE, 0);
    const touched = topicAggregates.reduce((sum, t) => sum + t.counts.TOUCHED, 0);
    const untouched = topicAggregates.reduce((sum, t) => sum + t.counts.UNTOUCHED, 0);
    const lastEdit = topicAggregates
      .map((t) => t.lastUpdated)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    return { totalQuestionnaires, complete, touched, untouched, lastEdit };
  }, [topicAggregates]);

  const overallProgress = useMemo(() => {
    const allApps = topicAggregates.flatMap((t) => t.applications || []);
    const { percent, weighted, total } = computeWeightedProgress(allApps as any, tasks as any);
    return { percent, weighted, total };
  }, [topicAggregates, tasks]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <div className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary-500 animate-spin" />
        <span>Lade...</span>
      </div>
    );
  if (error) return <div className="text-sm text-red-600">Fehler beim Laden: {error.message}</div>;
  if (!companies.length) return <div className="text-sm">Keine Tenants gefunden.</div>;

  return (
    <div className="space-y-10 w-full" data-testid="multi-tenant-report">
      <h2 className="text-xl font-semibold flex items-center gap-3 flex-wrap">
        <span>Report für:</span>
        <select
          className="dtInput rounded border border-gray-300 bg-white px-2 py-1 text-lg shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          value={companyId || ''}
          onChange={handleCompanyChange}
          data-testid="tenant-select"
          aria-label="Report für Tenant auswählen"
        >
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </h2>
      <div className="flex items-center gap-4 flex-wrap mt-10">
        <div className="flex items-center gap-2" title={`${overallProgress.percent}%`}>
          <div className="w-72 h-3 rounded bg-gray-200 overflow-hidden">
            <div
              className={`${overallProgress.percent === 100 ? 'bg-success-500' : overallProgress.percent > 0 ? 'bg-primary-root' : 'bg-gray-400'} h-full transition-all`}
              style={{ width: `${overallProgress.percent}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-gray-700">
            {overallProgress.weighted.toFixed(0)}/{overallProgress.total}
          </span>
          <span className="text-[10px] text-gray-500">Gesamtfortschritt</span>
        </div>
      </div>

      {overall && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-600">Übersicht (Fragebögen)</h3>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge label={`Gesamt: ${overall.totalQuestionnaires}`} />
            <Badge intent="success" label={`Vollständig: ${overall.complete}`} />
            <Badge intent="warning" label={`In Bearbeitung: ${overall.touched}`} />
            <Badge intent="neutral" label={`Unbearbeitet: ${overall.untouched}`} />
            <span className="text-gray-600 whitespace-nowrap">
              Letzte Änderung: {overall.lastEdit ? relativeTime(overall.lastEdit, user?.locale) : '—'}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {topicAggregates.map((topic) => {
          const isOpen = !!expanded[topic.id];
          return (
            <div
              key={topic.id}
              className="border border-gray-200 rounded-md bg-white shadow-sm overflow-hidden"
              data-testid={`topic-${topic.id}`}
            >
              <button
                type="button"
                onClick={() => toggleTopic(topic.id)}
                className="w-full flex items-start gap-4 px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <span
                  className={`mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center transition-transform ${isOpen ? 'rotate-90' : ''}`}
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" className="h-4 w-4 text-gray-500">
                    <path d="M7 5l6 5-6 5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <TopicProgress applications={topic.applications} tasks={tasks} />
                    <span className="text-sm font-medium text-gray-800 truncate" title={topic.title}>
                      {topic.title}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                    <span>
                      {topic.lastUpdated ? `Aktualisiert ${relativeTime(topic.lastUpdated, user?.locale)}` : '-'}
                    </span>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 bg-gray-50 px-11 py-3 text-sm space-y-2">
                  {topic.applications?.length ? (
                    <ul className="space-y-1">
                      {topic.applications.map((app) => {
                        const lastUpdated = (app.questionnaires || [])
                          .map((q) => q.updatedAt)
                          .filter(Boolean)
                          .sort()
                          .slice(-1)[0];
                        return (
                          <li key={app.id} className="flex items-center gap-3" data-testid={`application-${app.id}`}>
                            <StatusBadge status={calculateApplicationStatus(app, tasks)} />
                            <span className="text-xs font-medium text-gray-800">{app.title || app.id}</span>
                            <span className="text-[10px] text-gray-500">
                              {lastUpdated ? relativeTime(lastUpdated, user?.locale) : '—'}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="text-xs italic text-gray-500">Keine Applikationen</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function relativeTime(dateStr: string, userLocale?: string) {
  try {
    const locale = userLocale?.startsWith('de') ? de : enUS;
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale });
  } catch {
    return dateStr;
  }
}

// Calculate status for a single application based on its questionnaires (mirrors logic in topicStatus for one app)
// (application status logic moved to utils/progress)

function StatusBadge({ status }: { status: ApplicationStatus }) {
  const map: Record<ApplicationStatus, { label: string; className: string }> = {
    [ApplicationStatus.NOT_STARTED]: {
      label: 'Nicht gestartet',
      className: 'bg-gray-200 text-gray-700',
    },
    [ApplicationStatus.IN_PROGRESS]: {
      label: 'In Bearbeitung',
      className: 'bg-warning-100 text-warning-700',
    },
    [ApplicationStatus.COMPLETED]: {
      label: 'Abgeschlossen',
      className: 'bg-success-100 text-success-700',
    },
    [ApplicationStatus.DISABLED]: {
      label: 'Deaktiviert',
      className: 'bg-red-100 text-red-700',
    },
    [ApplicationStatus.PENDING]: {
      label: 'Ausstehend',
      className: 'bg-amber-100 text-amber-700',
    },
    [ApplicationStatus.EXPERIMENTAL]: {
      label: 'Experimentell',
      className: 'bg-purple-100 text-purple-700',
    },
  };
  const { label, className } = map[status];
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold ${className}`}>
      {label}
    </span>
  );
}

function Badge({ label, intent }: { label: string; intent?: 'success' | 'warning' | 'neutral' }) {
  const color =
    intent === 'success'
      ? 'bg-success-100 text-success-700'
      : intent === 'warning'
        ? 'bg-warning-100 text-warning-700'
        : intent === 'neutral'
          ? 'bg-gray-200 text-gray-700'
          : 'bg-primary-100 text-primary-700';
  return <span className={`inline-flex items-center rounded px-2 py-0.5 font-medium ${color}`}>{label}</span>;
}

function TopicProgress({ applications, tasks }: { applications: UserApplicationQuestionnaires[]; tasks?: Task[] }) {
  const total = applications?.length || 0;
  let accum = 0;
  if (total) {
    accum = applications.reduce((sum, app) => {
      const status = calculateApplicationStatus(app, tasks);
      if (status === ApplicationStatus.COMPLETED) return sum + 1;
      if (status === ApplicationStatus.IN_PROGRESS) return sum + 0.5;
      return sum;
    }, 0);
  }
  const fraction = total ? accum / total : 0;
  const percent = Math.round(fraction * 100);
  return (
    <div className="flex items-center gap-1" title={`${percent}%`}>
      <div className="w-24 h-2 rounded bg-gray-200 overflow-hidden">
        <div
          className={`${percent === 100 ? 'bg-success-500' : percent > 0 ? 'bg-warning-500' : 'bg-gray-400'} h-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-[10px] tabular-nums text-gray-600">
        {Math.round(accum * 10) / 10}/{total}
      </span>
    </div>
  );
}
